import { Box, Button, Divider, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

export interface GroupByOption<T extends string> {
  value: T;
  label: string;
}

export interface GroupByControlProps<T extends string> {
  value: T;
  onChange: (next: T) => void;
  options: GroupByOption<T>[];
  /** Defaults to "Group By". */
  label?: string;
  'aria-label'?: string;
}

const useStyles = makeStyles(theme => ({
  container: {
    display: 'inline-flex',
    alignItems: 'center',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 24,
    padding: theme.spacing(0.5),
  },
  label: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    padding: theme.spacing(0, 1.5),
  },
  divider: {
    height: 20,
    margin: theme.spacing(0, 0.5),
  },
  option: {
    textTransform: 'none',
    borderRadius: 20,
    minWidth: 'auto',
    padding: theme.spacing(0.5, 1.5),
  },
}));

/**
 * "Group By" pill control (Orca-style): a bordered pill housing a muted
 * "Group By" label followed by plain-text options, with the active option
 * rendered as a filled chip. Dividers between options are suppressed next
 * to the selected chip so it reads as its own visual boundary.
 */
export function GroupByControl<T extends string>({
  value,
  onChange,
  options,
  label = 'Group By',
  'aria-label': ariaLabel = 'Group security issues by',
}: GroupByControlProps<T>) {
  const classes = useStyles();
  return (
    <Box className={classes.container} role="group" aria-label={ariaLabel}>
      <Typography variant="body2" className={classes.label}>
        {label}
      </Typography>
      {options.map((option, index) => {
        const selected = option.value === value;
        const prevSelected = index > 0 && options[index - 1].value === value;
        return (
          <Box key={option.value} display="flex" alignItems="center">
            {index > 0 && !selected && !prevSelected && (
              <Divider orientation="vertical" className={classes.divider} />
            )}
            <Button
              type="button"
              className={classes.option}
              variant={selected ? 'contained' : 'text'}
              color={selected ? 'primary' : 'default'}
              disableElevation
              aria-pressed={selected}
              onClick={() => {
                if (!selected) onChange(option.value);
              }}
            >
              {option.label}
            </Button>
          </Box>
        );
      })}
    </Box>
  );
}
